import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Child, ClientFile } from "@visitproof/types";
import { ChildService } from "../services/ChildService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

// Re-type for backward compatibility
type File = ClientFile;

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface ChildContextProps {
  loading: Loadings;
  error: Errors;
  files: File[];
  childList: Child[];
  selectedChild: Child | null;
  setSelectedChild: (childId: string) => void;
  createChild: (
    childData: Omit<
      Child,
      "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "accountId"
    >,
  ) => Promise<string>;
  updateChild: (
    childId: string,
    childData: Partial<
      Omit<Child, "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy">
    >,
  ) => void;
  addFileToChild: (
    childId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">,
  ) => Promise<string>;
  deleteFileFromChild: (childId: string, fileId: string) => Promise<void>;
  deleteChildById: (childId: string) => void;
}

const ChildContext = createContext<ChildContextProps | undefined>(undefined);

export const useChild = (): ChildContextProps => {
  const context = useContext(ChildContext);
  if (!context) {
    throw new Error("useChild must be used within a ChildProvider");
  }
  return context;
};

export const ChildProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { account } = useAccount();
  const { user } = useAuth();
  const [files, setFiles] = useState<File[]>([]);
  const [childList, setChildList] = useState<Child[]>([]);
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);
  const [loading, setLoading] = useState<Loadings>({
    list: true,
    details: true,
  });
  const [error, setError] = useState<Errors>({});

  useEffect(() => {
    if (!user || !account?.id) {
      setLoading((prev) => ({ ...prev, list: false }));
      return;
    }
    setLoading((prev) => ({ ...prev, list: true }));
    setError((prev) => ({ ...prev, list: null }));
    const docRef = query(
      collection(db, "children"),
      where("accountId", "==", account.id),
      orderBy("lastName", "asc"),
      orderBy("firstName", "asc"),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        const children: Child[] = [];
        docSnapshot.forEach((doc) => {
          const childData = doc.data() as Child;
          children.push({ ...childData, id: doc.id });
        });
        setChildList(children);
        setLoading((prev) => ({ ...prev, list: false }));
      },
      (err) => {
        console.error("Error fetching cases: ", err);
        setError((prev) => ({ ...prev, list: err }));
        setLoading((prev) => ({ ...prev, list: false }));
      },
    );
    return () => {
      unsubscribe();
    };
  }, [account?.id, user]);

  const setSelectedChildById = async (childId: string) => {
    setLoading((prev) => ({ ...prev, details: true }));
    setError((prev) => ({ ...prev, details: null }));
    const child = childList.find((child) => child.id === childId);
    if (child) {
      setSelectedChild(child);
      setLoading((prev) => ({ ...prev, details: false }));
      return;
    }
    const docRef = doc(db, "children", childId);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const childData = docSnapshot.data() as Child;
          setSelectedChild({ ...childData, id: docSnapshot.id });
          setLoading((prev) => ({ ...prev, details: false }));
        } else {
          console.error("Child not found");
          setError((prev) => ({
            ...prev,
            details: new Error("Child not found"),
          }));
          setLoading((prev) => ({ ...prev, details: false }));
        }
      },
      (err) => {
        console.error("Error fetching child details: ", err);
        setError((prev) => ({ ...prev, details: err }));
        setLoading((prev) => ({ ...prev, details: false }));
      },
    );
    return () => {
      unsubscribe();
    };
  };

  useEffect(() => {
    if (!user || !selectedChild || !selectedChild.id) return;
    const childId = selectedChild.id;
    const getFiles = async () => {
      const files = await ChildService.getChildFiles(childId);
      setFiles(files);
    };
    getFiles();
  }, [user, selectedChild]);

  const createChild = async (
    childData: Omit<
      Child,
      "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "accountId"
    >,
  ): Promise<string> => {
    if (!account?.id) {
      throw new Error("Account ID is required to create a child");
    }
    const newChildId = await ChildService.createChild({
      ...childData,
      accountId: account.id,
      createdBy: user!.uid,
      updatedBy: user!.uid,
    });
    return newChildId;
  };

  const updateChild = async (
    childId: string,
    updatedData: Partial<Omit<Child, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await ChildService.updateChild(childId, {
      ...updatedData,
      updatedBy: user!.uid,
    });
    setSelectedChildById(childId);
  };

  const deleteChildById = async (childId: string) => {
    await ChildService.deleteChild(childId);
  };

  const addFileToChild = async (
    childId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">,
  ): Promise<string> => {
    const newFileId = await ChildService.addFileToChild(childId, {
      ...fileData,
      createdBy: user!.uid,
    });
    await setSelectedChildById(childId);
    return newFileId;
  };

  const deleteFileFromChild = async (
    childId: string,
    fileId: string,
  ): Promise<void> => {
    await ChildService.deleteFileFromChild(childId, fileId);
    await setSelectedChildById(childId);
  };

  return (
    <ChildContext.Provider
      value={{
        loading,
        error,
        files,
        childList,
        selectedChild,
        createChild,
        updateChild,
        deleteChildById,
        setSelectedChild: setSelectedChildById,
        addFileToChild,
        deleteFileFromChild,
      }}
    >
      {children}
    </ChildContext.Provider>
  );
};
